import { useState, useCallback, useEffect, useMemo } from 'react'
import { isCommonResponseSuccessful, isCommonResponseSuccessfulV2 } from 'src/mentor/helpers/utilityFunctions'
import { getMPDataWithoutParams, postMPDataWithoutParams } from 'src/mentor/services'
import { getSPDataWithParams, postSPDataWithParams } from 'src/supportPortal/services/services'
import { EMR_REPORT_TYPES, TAB_OPTIONS } from '../constants/emrConstants'

const createEmptyEmrEditorProperties = (reportType) => ({
  emrSections: [],
  emrReportType: reportType,
  specializations: [],
  tabIds: [],
  onEventCompletedTab: 'patient_history',
  onEventNotStartedTab: null,
  onEventInProgressTab: null,
})

const removeTransientEmrSectionFields = (section) => {
  if (!section || typeof section !== 'object') {
    return section
  }
  const sectionToSave = { ...section }
  delete sectionToSave.emrSectionResponse
  delete sectionToSave.runtime
  delete sectionToSave.usages
  return sectionToSave
}

const sanitizeEmrSettingsPayload = (settingsMap) => Object.keys(settingsMap || {}).reduce((acc, reportType) => {
  acc[reportType] = {
    ...settingsMap[reportType],
    emrSections: (settingsMap[reportType]?.emrSections || []).map(removeTransientEmrSectionFields),
  }
  return acc
}, {})

export const useEmrSettings = (config = {}) => {
  const {
    fetchApiSignature = '/api/secure/practitioner/clinical/emr-settings/emr-sections',
    saveApiSignature = '/api/secure/practitioner/emr-settings/emr-sections/save',
    approvedServicesMap,
    fetchCategories = true, // Only fetch categories if needed (for specializations form)
  } = config

  const [emrSettingsMap, setEmrSettingsMap] = useState()

  const [loading, setLoading] = useState(false)
  const [saveLoading, setSaveLoading] = useState(false)
  const [emrReportTypes, setEmrReportTypes] = useState([])

  const [categories, setCategories] = useState([])
  const [sectionSubsectionsMap, setSectionSubsectionsMap] = useState({}) // Map<sectionId, subsections[]>
  const [expandedSections, setExpandedSections] = useState(new Set()) // Track which sections are expanded

  const fetchEmrSettings = useCallback(async () => {
    setLoading(true)
    try {
      // Check if this is a support portal API
      const isSupportPortalApi = fetchApiSignature.includes('/api/secure/cc/support/')
      
      let response
      if (isSupportPortalApi) {
        // Parse query params from URL if present
        const urlParts = fetchApiSignature.split('?')
        const pathname = urlParts[0]
        const params = {}
        
        // Extract query params if present
        if (urlParts.length > 1) {
          const queryString = urlParts[1]
          const searchParams = new URLSearchParams(queryString)
          searchParams.forEach((value, key) => {
            params[key] = value
          })
        }
        
        // For editor page, always include includeAll=true
        // This ensures all sections are returned for the report type
        params.includeAll = 'true'
        
        response = await getSPDataWithParams(pathname, params)
        
        if (response?.status === 200 && isCommonResponseSuccessfulV2(response)) {
          const dataMap = response?.data?.data || response?.data || {}
          // Initialize extraExcludedSubSectionsInSections if not present
          const initializedDataMap = {}
          const newSectionSubsectionsMap = {}
          
          Object.keys(dataMap).forEach((reportType) => {
            initializedDataMap[reportType] = {
              ...dataMap[reportType],
              extraExcludedSubSectionsInSections: dataMap[reportType]?.extraExcludedSubSectionsInSections || {},
              extraIncludedSubSectionsInSections: dataMap[reportType]?.extraIncludedSubSectionsInSections || {},
            }
            
            // Extract subsections from each section
            // Note: The server resolves subSections[].selected based on extraIncludedSubSectionsInSections 
            // and extraExcludedSubSectionsInSections maps, so we trust the selected flags from the API
            if (initializedDataMap[reportType]?.emrSections) {
              initializedDataMap[reportType].emrSections.forEach((section) => {
                const sectionId = section?.id
                const sectionCode = section?.defCode || sectionId
                
                // Try to get subsections from multiple possible locations
                let subsections = []
                
                // 1. Direct subSections array (e.g., diagnosis, drug_advice)
                if (section?.subSections && Array.isArray(section.subSections)) {
                  subsections = section.subSections
                }
                // 2. Nested in emrSectionResponse.section.subSections (e.g., screening-recommendation, patient-history-notes)
                else if (section?.emrSectionResponse?.section?.subSections && Array.isArray(section.emrSectionResponse.section.subSections)) {
                  subsections = section.emrSectionResponse.section.subSections
                }
                
                if (subsections.length > 0) {
                  newSectionSubsectionsMap[sectionCode] = subsections
                }
              })
            }
          })
          
          setEmrSettingsMap(initializedDataMap)
          setSectionSubsectionsMap(newSectionSubsectionsMap)
        }
      } else {
        // Mentor portal API
        // For editor page, always include includeAll=true
        // This ensures all sections are returned for the report type
        const urlParts = fetchApiSignature.split('?')
        const pathname = urlParts[0]
        const params = new URLSearchParams()
        
        // Extract existing query params if present
        if (urlParts.length > 1) {
          const existingParams = new URLSearchParams(urlParts[1])
          existingParams.forEach((value, key) => {
            params.append(key, value)
          })
        }
        
        // Add includeAll=true for editor page
        params.append('includeAll', 'true')
        
        const apiUrl = `${pathname}?${params.toString()}`
        response = await getMPDataWithoutParams(apiUrl)

        if (response?.status === 200 && isCommonResponseSuccessful(response?.data?.code)) {
          const dataMap = response?.data?.data || {}
          // Initialize extraExcludedSubSectionsInSections if not present
          const initializedDataMap = {}
          const newSectionSubsectionsMap = {}
          
          Object.keys(dataMap).forEach((reportType) => {
            initializedDataMap[reportType] = {
              ...dataMap[reportType],
              extraExcludedSubSectionsInSections: dataMap[reportType]?.extraExcludedSubSectionsInSections || {},
              extraIncludedSubSectionsInSections: dataMap[reportType]?.extraIncludedSubSectionsInSections || {},
            }
            
            // Extract subsections from each section
            // Note: The server resolves subSections[].selected based on extraIncludedSubSectionsInSections 
            // and extraExcludedSubSectionsInSections maps, so we trust the selected flags from the API
            if (initializedDataMap[reportType]?.emrSections) {
              initializedDataMap[reportType].emrSections.forEach((section) => {
                const sectionId = section?.id
                const sectionCode = section?.defCode || sectionId
                
                // Try to get subsections from multiple possible locations
                let subsections = []
                
                // 1. Direct subSections array (e.g., diagnosis, drug_advice)
                if (section?.subSections && Array.isArray(section.subSections)) {
                  subsections = section.subSections
                }
                // 2. Nested in emrSectionResponse.section.subSections (e.g., screening-recommendation, patient-history-notes)
                else if (section?.emrSectionResponse?.section?.subSections && Array.isArray(section.emrSectionResponse.section.subSections)) {
                  subsections = section.emrSectionResponse.section.subSections
                }
                
                if (subsections.length > 0) {
                  newSectionSubsectionsMap[sectionCode] = subsections
                }
              })
            }
          })
          
          setEmrSettingsMap(initializedDataMap)
          setSectionSubsectionsMap(newSectionSubsectionsMap)
        }
      }
    } catch (error) {
      console.error('Failed to fetch EMR settings:', error)
    } finally {
      setLoading(false)
    }
  }, [fetchApiSignature])

  const saveEmrSettings = useCallback(async () => {
    setSaveLoading(true)
    try {
      // Ensure screening report section is always selected before saving
      const settingsToSave = Object.keys(emrSettingsMap || {}).reduce((acc, reportType) => {
        acc[reportType] = { ...emrSettingsMap[reportType] }
        return acc
      }, {})
      Object.keys(settingsToSave).forEach((reportType) => {
        if (settingsToSave[reportType]?.emrSections) {
          settingsToSave[reportType].emrSections = settingsToSave[reportType].emrSections.map((section) => {
            if (section?.id === 'screeningReportData') {
              return { ...section, selected: true, enabled: true }
            }
            
            // Merge subsection printable settings from sectionSubsectionsMap into the section's subSections
            // According to API docs, subsection printable is saved via emrSections[].subSections[].printable
            const sectionCode = section?.defCode || section?.id
            const subsectionsFromMap = sectionSubsectionsMap[sectionCode] || []
            
            let updatedSection = { ...section }
            
            if (subsectionsFromMap.length > 0) {
              // Update direct subSections array
              if (section.subSections && Array.isArray(section.subSections)) {
                const updatedSubSections = section.subSections.map((subSection) => {
                  const subsectionKey = subSection?.code || subSection?.uid
                  const subsectionFromMap = subsectionsFromMap.find(
                    (ss) => (ss?.code || ss?.uid) === subsectionKey
                  )
                  if (subsectionFromMap) {
                    // carry over BOTH printable and selected so the saved map reflects
                    // every drawer interaction — the caller pushes this map straight
                    // into Redux, so the report page updates without waiting on a refetch
                    return {
                      ...subSection,
                      ...(subsectionFromMap.printable !== undefined
                        ? { printable: subsectionFromMap.printable }
                        : {}),
                      ...(subsectionFromMap.selected !== undefined
                        ? { selected: subsectionFromMap.selected }
                        : {}),
                    }
                  }
                  return subSection
                })
                updatedSection = { ...updatedSection, subSections: updatedSubSections }
              }
              
              // Update nested emrSectionResponse.section.subSections if it exists
              if (section.emrSectionResponse?.section?.subSections) {
                const updatedNestedSubSections = section.emrSectionResponse.section.subSections.map((subSection) => {
                  const subsectionKey = subSection?.code || subSection?.uid
                  const subsectionFromMap = subsectionsFromMap.find(
                    (ss) => (ss?.code || ss?.uid) === subsectionKey
                  )
                  if (subsectionFromMap) {
                    // carry over BOTH printable and selected so the saved map reflects
                    // every drawer interaction — the caller pushes this map straight
                    // into Redux, so the report page updates without waiting on a refetch
                    return {
                      ...subSection,
                      ...(subsectionFromMap.printable !== undefined
                        ? { printable: subsectionFromMap.printable }
                        : {}),
                      ...(subsectionFromMap.selected !== undefined
                        ? { selected: subsectionFromMap.selected }
                        : {}),
                    }
                  }
                  return subSection
                })
                updatedSection = {
                  ...updatedSection,
                  emrSectionResponse: {
                    ...section.emrSectionResponse,
                    section: {
                      ...section.emrSectionResponse.section,
                      subSections: updatedNestedSubSections,
                    },
                  },
                }
              }
            }
            
            return updatedSection
          })
        }
        // Ensure subsection override maps are included in the payload
        // According to API docs, we can send either:
        // 1. Explicit maps: extraIncludedSubSectionsInSections and/or extraExcludedSubSectionsInSections
        // 2. Derived from payload: emrSections[].subSections[].selected flags (server will derive maps)
        // We're using explicit maps approach for clarity
        if (!settingsToSave[reportType].extraExcludedSubSectionsInSections) {
          settingsToSave[reportType].extraExcludedSubSectionsInSections = {}
        }
        if (!settingsToSave[reportType].extraIncludedSubSectionsInSections) {
          settingsToSave[reportType].extraIncludedSubSectionsInSections = {}
        }
      })
      
      const apiSettingsToSave = sanitizeEmrSettingsPayload(settingsToSave)

      // Check if this is a support portal API
      const isSupportPortalApi = saveApiSignature.includes('/api/secure/cc/support/')
      
      let response
      if (isSupportPortalApi) {
        // Parse query params from URL if present
        const urlParts = saveApiSignature.split('?')
        const pathname = urlParts[0]
        const params = {}
        
        // Extract query params if present
        if (urlParts.length > 1) {
          const queryString = urlParts[1]
          const searchParams = new URLSearchParams(queryString)
          searchParams.forEach((value, key) => {
            params[key] = value
          })
        }
        
        response = await postSPDataWithParams(pathname, apiSettingsToSave, params)
        
        if (response?.status === 200 && isCommonResponseSuccessfulV2(response)) {
          // Return saved data so parent can sync Redux/report page immediately without refetch/flicker
          return settingsToSave
        }
        return false
      } else {
        // Mentor portal API
        response = await postMPDataWithoutParams(saveApiSignature, apiSettingsToSave)

        if (response?.status === 200 && isCommonResponseSuccessful(response?.data?.code)) {
          // Return saved data so parent can sync Redux/report page immediately without refetch/flicker
          return settingsToSave
        }
        return false
      }
    } catch (error) {
      console.error('Failed to save EMR settings:', error)
      return false
    } finally {
      setSaveLoading(false)
    }
  }, [saveApiSignature, emrSettingsMap, sectionSubsectionsMap])

  const updateSectionsForReportType = useCallback((reportType, newSections) => {
    setEmrSettingsMap((prev) => ({
      ...prev,
      [reportType]: {
        ...prev[reportType],
        emrSections: newSections,
      },
    }))
  }, [])

  const toggleSectionForReportType = useCallback((reportType, sectionItem, checked) => {
    // Prevent disabling screening report section - it should always be enabled when data exists
    if (sectionItem?.id === 'screeningReportData' && !checked) {
      // Don't allow disabling - keep it selected
      return
    }
    
    setEmrSettingsMap((prev) => ({
      ...prev,
      [reportType]: {
        ...prev[reportType],
        emrSections: prev[reportType].emrSections.map((section) =>
          section?.id === sectionItem?.id ? { ...section, selected: checked } : section,
        ),
      },
    }))
  }, [])

  const togglePrintableForReportType = useCallback((reportType, sectionItem) => {
    setEmrSettingsMap((prev) => ({
      ...prev,
      [reportType]: {
        ...prev[reportType],
        emrSections: prev[reportType].emrSections.map((section) =>
          section?.id === sectionItem?.id
            ? { ...section, printable: !section?.printable }
            : section,
        ),
      },
    }))
  }, [])

  const updateSectionPrintOnlyEditorOnlyForReportType = useCallback((reportType, sectionItem, { printOnly, editorOnly }) => {
    setEmrSettingsMap((prev) => ({
      ...prev,
      [reportType]: {
        ...prev[reportType],
        emrSections: prev[reportType].emrSections.map((section) =>
          section?.id === sectionItem?.id
            ? { ...section, printOnly: !!printOnly, editorOnly: !!editorOnly }
            : section,
        ),
      },
    }))
  }, [])

  const reorderSectionsForReportType = useCallback((reportType, dragResult) => {
    if (!dragResult.destination) return

    setEmrSettingsMap((prev) => {
      const sections = Array.from(prev[reportType].emrSections)
      const sourceIndex = dragResult.source.index
      const movedItem = sections[sourceIndex]
      
      // Prevent moving screening report section
      if (movedItem?.id === 'screeningReportData') {
        return prev
      }
      
      const [removedItem] = sections.splice(sourceIndex, 1)
      sections.splice(dragResult.destination.index, 0, removedItem)

      return {
        ...prev,
        [reportType]: {
          ...prev[reportType],
          emrSections: sections,
        },
      }
    })
  }, [])

  const updateFieldForReportType = useCallback((reportType, fieldName, value) => {
    setEmrSettingsMap((prev) => ({
      ...prev,
      [reportType]: {
        ...prev[reportType],
        [fieldName]: value,
      },
    }))
  }, [])

  // Fetch section effective layout to get subsections
  const fetchSectionEffectiveLayout = useCallback(async (sectionCode, reportType) => {
    try {
      const isSupportPortalApi = fetchApiSignature.includes('/api/secure/cc/support/')
      
      // Extract practitioner params from fetchApiSignature if support portal
      let params = {}
      if (isSupportPortalApi) {
        const urlParts = fetchApiSignature.split('?')
        if (urlParts.length > 1) {
          const queryString = urlParts[1]
          const searchParams = new URLSearchParams(queryString)
          searchParams.forEach((value, key) => {
            params[key] = value
          })
        }
      }
      
      let response
      if (isSupportPortalApi) {
        // Support portal API for section effective
        const apiPath = `/api/secure/cc/support/practitioner/emr/sections/${sectionCode}/effective`
        response = await getSPDataWithParams(apiPath, params)
      } else {
        // Mentor portal API
        const apiPath = `/api/secure/practitioner/emr/sections/${sectionCode}/effective`
        response = await getMPDataWithoutParams(apiPath)
      }
      
      if (response?.status === 200) {
        const isSupportResponse = isSupportPortalApi && isCommonResponseSuccessfulV2(response)
        const isMentorResponse = !isSupportPortalApi && isCommonResponseSuccessful(response?.data?.code)
        
        if (isSupportResponse || isMentorResponse) {
          const sectionData = isSupportPortalApi 
            ? (response?.data?.data || response?.data)
            : (response?.data?.data || {})
          
          const subsections = sectionData?.section?.subSections || []
          
          setSectionSubsectionsMap((prev) => ({
            ...prev,
            [sectionCode]: subsections,
          }))
          
          return subsections
        }
      }
      // resolve the section to "no subsections" so the UI stops offering to load
      // them (otherwise the "Click to load subsections" hint dangles forever)
      setSectionSubsectionsMap((prev) =>
        prev[sectionCode] === undefined ? { ...prev, [sectionCode]: [] } : prev,
      )
      return []
    } catch (error) {
      console.error(`Failed to fetch section layout for ${sectionCode}:`, error)
      setSectionSubsectionsMap((prev) =>
        prev[sectionCode] === undefined ? { ...prev, [sectionCode]: [] } : prev,
      )
      return []
    }
  }, [fetchApiSignature])

  // Toggle subsection exclusion
  // According to API docs, subsection keys are resolved as: 1) code (preferred), 2) uid
  // Section keys are resolved as: 1) defCode (preferred), 2) id
  const toggleSubsectionExclusion = useCallback((reportType, sectionId, subsectionUid, isExcluded) => {
    setEmrSettingsMap((prev) => {
      const currentSettings = prev[reportType] || {}
      const currentExclusions = currentSettings.extraExcludedSubSectionsInSections || {}
      
      // Use section defCode if available, otherwise use id (per API docs)
      const section = currentSettings?.emrSections?.find(s => s.id === sectionId)
      const sectionKey = section?.defCode || sectionId
      
      const sectionExclusions = currentExclusions[sectionKey] || []
      
      let newSectionExclusions
      if (isExcluded) {
        // Add to exclusions if not already present
        newSectionExclusions = sectionExclusions.includes(subsectionUid)
          ? sectionExclusions
          : [...sectionExclusions, subsectionUid]
      } else {
        // Remove from exclusions
        newSectionExclusions = sectionExclusions.filter((uid) => uid !== subsectionUid)
      }
      
      // If no exclusions left for this section, remove the section key
      const newExclusions = { ...currentExclusions }
      if (newSectionExclusions.length === 0) {
        delete newExclusions[sectionKey]
      } else {
        newExclusions[sectionKey] = newSectionExclusions
      }
      
      const updatedSettings = {
        ...prev,
        [reportType]: {
          ...currentSettings,
          extraExcludedSubSectionsInSections: newExclusions,
        },
      }
      
      // Also update sectionSubsectionsMap to reflect the change immediately in UI
      // Use the section from the updated state
      const updatedSection = updatedSettings[reportType]?.emrSections?.find(s => s.id === sectionId)
      const sectionCode = updatedSection?.defCode || sectionId
      
      setSectionSubsectionsMap((prevSubsections) => {
        if (!prevSubsections[sectionCode]) {
          return prevSubsections
        }
        
        const subsections = prevSubsections[sectionCode]
        const updatedSubsections = subsections.map((subsection) => {
          const subsectionKey = subsection?.code || subsection?.uid
          if (subsectionKey === subsectionUid) {
            return { ...subsection, selected: !isExcluded }
          }
          return subsection
        })
        
        return {
          ...prevSubsections,
          [sectionCode]: updatedSubsections,
        }
      })
      
      return updatedSettings
    })
  }, [])

  // Toggle subsection printable
  // According to API docs, subsection keys are resolved as: 1) code (preferred), 2) uid
  // Section keys are resolved as: 1) defCode (preferred), 2) id
  // Subsection printable settings are saved via emrSections[].subSections[].printable in the payload
  const toggleSubsectionPrintable = useCallback((reportType, sectionId, subsectionUid) => {
    // Update sectionSubsectionsMap for immediate UI update
    setSectionSubsectionsMap((prev) => {
      // Find the section to get its defCode or id
      const section = emrSettingsMap?.[reportType]?.emrSections?.find(s => s.id === sectionId)
      if (!section) {
        return prev
      }
      
      // Use defCode if available, otherwise use id (per API docs)
      const sectionCode = section?.defCode || sectionId
      
      if (!prev[sectionCode]) {
        return prev
      }
      
      const subsections = prev[sectionCode]
      const updatedSubsections = subsections.map((subsection) => {
        // Match by code (preferred) or uid
        const subsectionKey = subsection?.code || subsection?.uid
        if (subsectionKey === subsectionUid) {
          const newPrintable = !(subsection?.printable !== false)
          return { ...subsection, printable: newPrintable }
        }
        return subsection
      })
      
      return {
        ...prev,
        [sectionCode]: updatedSubsections,
      }
    })
    
    // Also update emrSettingsMap to include the printable setting in the payload
    // According to API docs, subsection printable is saved via emrSections[].subSections[].printable
    setEmrSettingsMap((prev) => {
      const currentSettings = prev[reportType] || {}
      const updatedSections = (currentSettings.emrSections || []).map((section) => {
        if (section.id === sectionId) {
          // Update the section's subSections array with the new printable value
          const updatedSubSections = (section.subSections || []).map((subSection) => {
            const subsectionKey = subSection?.code || subSection?.uid
            if (subsectionKey === subsectionUid) {
              return { ...subSection, printable: !(subSection?.printable !== false) }
            }
            return subSection
          })
          
          // Also check emrSectionResponse.section.subSections if it exists
          let updatedEmrSectionResponse = section.emrSectionResponse
          if (section.emrSectionResponse?.section?.subSections) {
            const updatedNestedSubSections = section.emrSectionResponse.section.subSections.map((subSection) => {
              const subsectionKey = subSection?.code || subSection?.uid
              if (subsectionKey === subsectionUid) {
                return { ...subSection, printable: !(subSection?.printable !== false) }
              }
              return subSection
            })
            updatedEmrSectionResponse = {
              ...section.emrSectionResponse,
              section: {
                ...section.emrSectionResponse.section,
                subSections: updatedNestedSubSections,
              },
            }
          }
          
          return {
            ...section,
            subSections: updatedSubSections.length > 0 ? updatedSubSections : section.subSections,
            emrSectionResponse: updatedEmrSectionResponse,
          }
        }
        return section
      })
      
      return {
        ...prev,
        [reportType]: {
          ...currentSettings,
          emrSections: updatedSections,
        },
      }
    })
  }, [emrSettingsMap])

  // Toggle section expansion
  const toggleSectionExpansion = useCallback((sectionId, sectionCode, reportType) => {
    setExpandedSections((prev) => {
      const newSet = new Set(prev)
      if (newSet.has(sectionId)) {
        newSet.delete(sectionId)
      } else {
        newSet.add(sectionId)
        // Fetch subsections when expanding
        if (!sectionSubsectionsMap[sectionCode]) {
          fetchSectionEffectiveLayout(sectionCode, reportType)
        }
      }
      return newSet
    })
  }, [sectionSubsectionsMap, fetchSectionEffectiveLayout])

  const getEmrReportTypes = useCallback(async () => {
    try {
      const resp = await getMPDataWithoutParams('/api/health/emr-report-types')
      if (resp?.status === 200) {
        setEmrReportTypes(resp?.data || [])
      }
    } catch (error) {
      console.error('Failed to fetch EMR report types:', error)
      setEmrReportTypes([])
    }
  }, [])

  async function getMetaCategories() {
    const resp = await getMPDataWithoutParams('/api/secure/teacher/meta-categories')
    if (resp.status === 200) {
      setCategories(resp.data || [])
    }
  }

  useEffect(() => {
    getEmrReportTypes()
  }, [getEmrReportTypes])

  useEffect(() => {
    // Only fetch categories if they're needed (for specializations form in EmrReportSettingsForm)
    if (fetchCategories) {
      getMetaCategories()
    }
  }, [fetchCategories])


  const reportTypesToReturn = useMemo(() => {
    let allowedReportTypes = (
      Object.entries(approvedServicesMap || {}).map(
        ([key, approvedServices]) => approvedServices?.emrBasedReportTypes,
      ) || []
    ).flat()
    allowedReportTypes = [...new Set(allowedReportTypes)]?.filter(type=>!!type)
    return allowedReportTypes.map((reportTypeId, idx) => {
      let foundMainTitleBasedOnReportType =
        emrReportTypes?.find((type) => type?.id === reportTypeId)?.title || ''
      // const specTitles = approvedServices?.specializations?.map((specId) => {
      //   const spec = categories?.find((cat) => cat?.id === specId)
      //   return spec?.title || ''
      // })
      // let specsTitle = specTitles?.join(', ') || ''
      // if (!!specsTitle) {
      //   specsTitle = `(${specsTitle})`
      // }
  
      return {
        id: reportTypeId,
        specializationIds: [],
        reportType: reportTypeId,
        title: foundMainTitleBasedOnReportType,
      }
    })
  }, [emrReportTypes, approvedServicesMap])

  return {
    emrSettingsMap,
    loading,
    saveLoading,
    fetchEmrSettings,
    saveEmrSettings,
    updateSectionsForReportType,
    toggleSectionForReportType,
    togglePrintableForReportType,
    updateSectionPrintOnlyEditorOnlyForReportType,
    reorderSectionsForReportType,
    updateFieldForReportType,
    EMR_REPORT_TYPES,
    emrReportTypes: reportTypesToReturn,
    metaEmrReportTypes: emrReportTypes,
    categories,
    TAB_OPTIONS,
    sectionSubsectionsMap,
    expandedSections,
    toggleSectionExpansion,
    toggleSubsectionExclusion,
    toggleSubsectionPrintable,
    fetchSectionEffectiveLayout,
  }
}
